import React from "react";
import {
  ResponsiveContainer,
  BarChart,
  LineChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip,
  // LabelList, // No longer used
} from "recharts";

export default function BaseChart({
  data = [],
  xKey = "name",
  yKey = "value",
  type = "bar", // "bar" or "line"
  color = "#2563eb",
  height = 300,
  xAxisFormatter,
  yAxisFormatter,
  xLabel = "",
  yLabel = "",
  tooltipFormatter,
  // Optional custom legend label (used by post-analysis charts)
  legendLabel,
  children,
  ...rest
}) {
  // Truncate long x-axis labels
  const truncate = (label) =>
    typeof label === "string" && label.length > 14 ? label.slice(0, 12) + "…" : label;

  return (
    <div className="bg-background rounded-lg">
      <ResponsiveContainer width="100%" height={height}>
        {type === "bar" ? (
          <BarChart
            data={data}
            margin={{ top: 30, right: 30, left: 20, bottom: 90 }}
            {...rest}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey={xKey}
              tickFormatter={truncate}
              angle={-35}
              textAnchor="end"
              interval={0}
              height={60}
              tick={{ fontSize: 14, fill: "hsl(var(--foreground))" }}
              label={{
                value: xLabel || xKey.charAt(0).toUpperCase() + xKey.slice(1),
                position: "insideBottom",
                offset: 6,
                fontWeight: "bold",
                fontSize: 14,
                fill: "hsl(var(--foreground))",
                dy: 40,
              }}
            />
            <YAxis
              tickFormatter={yAxisFormatter}
              tick={{ fontSize: 14, fill: "hsl(var(--foreground))" }}
              label={{
                value: yLabel || yKey.charAt(0).toUpperCase() + yKey.slice(1),
                angle: -90,
                position: "left",
                offset: 60,
                fontWeight: "bold",
                fontSize: 16,
                fill: "hsl(var(--foreground))"
              }}
            />
            <Tooltip
              cursor={{ fill: "hsl(var(--muted))", opacity: 0.2 }}
              contentStyle={{
                borderRadius: 8,
                border: "1px solid hsl(var(--border))",
                fontSize: 14,
                fontWeight: 500,
                background: "hsl(var(--background))",
                color: "hsl(var(--foreground))"
              }}
              formatter={tooltipFormatter ? (value, name) => [tooltipFormatter(value), name] : undefined}
            />
            <Legend 
              verticalAlign="top" 
              height={36}
              wrapperStyle={{
                color: "hsl(var(--foreground))"
              }}
              {...(legendLabel
                ? {
                    payload: [
                      {
                        value: legendLabel || yLabel || yKey.charAt(0).toUpperCase() + yKey.slice(1),
                        type: "square",
                        id: "value",
                        color: color
                      }
                    ]
                  }
                : {})}
            />
            <Bar
              dataKey={yKey}
              fill={color}
              radius={[8, 8, 0, 0]}
              fillOpacity={0.95}
            />
            {children}
          </BarChart>
        ) : (
          <LineChart
            data={data}
            margin={{ top: 30, right: 30, left: 20, bottom: 90 }}
            {...rest}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
            <XAxis
              dataKey={xKey}
              tickFormatter={truncate}
              angle={-35}
              textAnchor="end"
              interval={0}
              height={60}
              tick={{ fontSize: 14, fill: "hsl(var(--foreground))" }}
              label={{
                value: xLabel || xKey.charAt(0).toUpperCase() + xKey.slice(1),
                position: "insideBottom",
                offset: 6,
                fontWeight: "bold",
                fontSize: 14,
                fill: "hsl(var(--foreground))",
                dy: 40,
              }}
            />
            <YAxis
              tickFormatter={yAxisFormatter}
              tick={{ fontSize: 14, fill: "hsl(var(--foreground))" }}
              label={{
                value: yLabel || yKey.charAt(0).toUpperCase() + yKey.slice(1),
                angle: -90,
                position: "left",
                offset: 60,
                fontWeight: "bold",
                fontSize: 16,
                fill: "hsl(var(--foreground))"
              }}
            />
            <Tooltip
              cursor={{ fill: "hsl(var(--muted))", opacity: 0.2 }}
              contentStyle={{
                borderRadius: 8,
                border: "1px solid hsl(var(--border))",
                fontSize: 14,
                fontWeight: 500,
                background: "hsl(var(--background))",
                color: "hsl(var(--foreground))"
              }}
              formatter={tooltipFormatter ? (value, name) => [tooltipFormatter(value), name] : undefined}
            />
            <Legend 
              verticalAlign="top" 
              height={36}
              wrapperStyle={{
                color: "hsl(var(--foreground))"
              }}
              {...(legendLabel
                ? {
                    payload: [
                      {
                        value: legendLabel || yLabel || yKey.charAt(0).toUpperCase() + yKey.slice(1),
                        type: "square",
                        id: "value",
                        color: color
                      }
                    ]
                  }
                : {})}
            />
            <Line 
              type="monotone" 
              dataKey={yKey} 
              stroke={color} 
              strokeWidth={3} 
              dot={{ r: 4, fill: color }} 
            />
            {children}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
} 